import { Badge, Button, Flex, Select, Text, Tooltip } from "@mantine/core";
import { last, map } from "lodash";
import { DataTableColumn } from "mantine-datatable";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import ActionIcon from "../../../../components/ActionIcon/ActionIcon.tsx";
import { Variant } from "../../../../components/ActionIcon/types.ts";
import CPExceptionTableCard from "../../../../components/CPExceptionTableCard/CPExceptionTableCard.tsx";
import { DatabaseMetadataPopover } from "../../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import SvgEdit from "../../../../components/Icons/Edit.tsx";
import InfoTooltip from "../../../../components/InfoTooltip/InfoTooltip.tsx";
import Spoiler from "../../../../components/Spoiler/Spoiler.tsx";
import { TableData, TableRow } from "../../../../components/Table/types.ts";
import TableFilesDisplay from "../../../../components/TableFilesDisplay/TableFilesDisplay.tsx";
import { boolSelectorOptions } from "../../../../constants.ts";
import {
  CollateralStatus,
  PaymentMethod,
  PaymentType,
  TransactionHistoryOrderStatus,
  TransactionHistoryStatus,
  UpdateTransactionHistoryInput,
} from "../../../../graphql/generated.ts";
import {
  orderStatusToHumanReadable,
  TransactionHistory,
} from "../../../../types/transactionHistory.ts";
import { booleanToString, stringToBoolean } from "../../../../utils/boolean.ts";
import { toLocalDate } from "../../../../utils/date.ts";
import { toCurrencyPrice } from "../../../../utils/number.ts";
import { toHumanReadable } from "../../../../utils/string.ts";
import { InvoicesModal } from "../InvoicesModal.tsx";
import { PODocsModal } from "../PODocsModal.tsx";
import { useStyles } from "../TransactionHistoryList.styles.ts";
import { ModalType } from "../types.ts";

interface Props {
  data: TransactionHistory[] | null | undefined;
  canEdit: boolean;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onOpenEditModal: (type: ModalType, value: TransactionHistory) => void;
  onInlineEdit: (id: number, input: UpdateTransactionHistoryInput) => void;
  onChangeCollateral: (value: TransactionHistory) => void;
}

export const useTransactionHistoryTableData = ({
  data,
  canEdit,
  onApprove,
  onReject,
  onOpenEditModal,
  onInlineEdit,
  onChangeCollateral,
}: Props): TableData => {
  const { classes } = useStyles();

  const navigate = useNavigate();

  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "orderDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "orderDate",
          }}
        >
          Order Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "quotationNo",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "quotationNo",
          }}
        >
          Quotation No
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 300,
    },
    {
      accessor: "invoiceNumber",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "invoiceNumber",
          }}
        >
          Invoice No
        </DatabaseMetadataPopover>
      ),
      width: 300,
    },
    {
      accessor: "saleOrderNumber",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "saleOrderNumber",
          }}
        >
          SO No
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 300,
    },
    {
      accessor: "salesName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "salesName",
          }}
        >
          Sales Name
        </DatabaseMetadataPopover>
      ),
      width: 200,
    },
    {
      accessor: "salesTeam",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "salesTeam",
          }}
        >
          Sales Team
        </DatabaseMetadataPopover>
      ),
      width: 200,
    },
    {
      accessor: "limitUsed",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Limit",
            fieldName: "limitName",
          }}
        >
          Limit Used
        </DatabaseMetadataPopover>
      ),
      width: 300,
    },
    {
      accessor: "paymentType",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "paymentType",
          }}
        >
          Payment Type
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "poData",
      title: "PO Docs",
    },
    {
      accessor: "paymentMethod",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "paymentMethod",
          }}
        >
          Payment Method
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "orderStatus",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "orderStatus",
          }}
        >
          Order Status
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "amountTotal",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "totalOrderAmount",
          }}
        >
          Total Order Amount
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 200,
    },
    {
      accessor: "paymasters",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "paymasters",
          }}
        >
          Paymaster Name
        </DatabaseMetadataPopover>
      ),
      width: "200px",
    },
    {
      accessor: "bastDocuments",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "bastDocuments",
          }}
        >
          BAST Files
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "transactionState",
      title: "Transaction State",
    },
    {
      accessor: "hasException",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "hasException",
          }}
        >
          <Flex align="center">
            <Text>Has Exceptions</Text>
            <InfoTooltip tooltipText="Only COD payment type can be changed." />
          </Flex>
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "isRestructured",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "isRestructured",
          }}
        >
          Is Restructured
        </DatabaseMetadataPopover>
      ),
      sortable: false,
    },
    {
      accessor: "conditionPrecedents",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "conditionPrecedents",
          }}
        >
          <Flex align="center">
            <Text>Condition Precedents</Text>
            <InfoTooltip tooltipText="You can add an condition precedent only if a limit exists in the transaction history record." />
          </Flex>
        </DatabaseMetadataPopover>
      ),
      sortable: false,
      width: 300,
    },
    {
      accessor: "exceptions",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "exceptions",
          }}
        >
          <Flex align="center">
            <Text>Exceptions</Text>
            <InfoTooltip tooltipText="You can add an exception only if a limit exists in the transaction history record." />
          </Flex>
        </DatabaseMetadataPopover>
      ),
      sortable: false,
      width: 300,
    },
    {
      accessor: "notes",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "notes",
          }}
        >
          Notes
        </DatabaseMetadataPopover>
      ),
      width: 300,
    },
    {
      accessor: "repayment",
    },
    {
      accessor: "approval",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TransactionHistory",
            fieldName: "status",
          }}
        >
          <Flex align="center">
            <Text>Approval</Text>
            <InfoTooltip tooltipText="Only TOP payment type can be approved or rejected" />
          </Flex>
        </DatabaseMetadataPopover>
      ),
      width: 400,
      hidden: !canEdit,
    },
  ];

  const handleOpenEditModal = useCallback(
    (type: ModalType, value: TransactionHistory) => {
      if (!canEdit) {
        return;
      }
      onOpenEditModal(type, value);
    },
    [canEdit, onOpenEditModal]
  );

  const handleInlineEdit = useCallback(
    (id: number, input: UpdateTransactionHistoryInput) => {
      if (!canEdit) {
        return;
      }
      onInlineEdit(id, input);
    },
    [canEdit, onInlineEdit]
  );

  const getExceptionColumn = useCallback(
    (item: TransactionHistory) => {
      if (canEdit && item.paymentType === PaymentType.Cod) {
        return (
          <Select
            data={boolSelectorOptions}
            value={item?.hasException?.toString()}
            withinPortal
            zIndex={100}
            size="s"
            onChange={(value) =>
              handleInlineEdit(item.id, {
                hasException: stringToBoolean(value, true),
              })
            }
          />
        );
      }
      return item.hasException ? "True" : "False";
    },
    [canEdit, handleInlineEdit]
  );

  const getApprovalDisabledTooltipText = useCallback(
    (item: TransactionHistory) => {
      if (item.paymentMethod === PaymentMethod.Transfer) {
        return "";
      }
      if (item.paymentType !== PaymentType.Top) {
        return "Only TOP payment type can be approved or rejected.";
      }
      if (item.orderStatus !== TransactionHistoryOrderStatus.PoSubmission) {
        return "Status is not PO Submission.";
      }
      const collateral = last(item.collateral);
      if (!collateral) {
        return "";
      }
      if (!collateral.status) {
        return "Needs confirmation on Collateral Status.";
      }
      if (collateral.status === CollateralStatus.Reject) {
        return "Collateral is Rejected.";
      }
      return "";
    },
    []
  );

  const renderRepayment = useCallback(
    (item: TransactionHistory) => <InvoicesModal data={item} />,
    []
  );

  const renderApproval = useCallback(
    (item: TransactionHistory) => {
      if (item.status === TransactionHistoryStatus.Approved) {
        return (
          <Badge variant="active" w="80%" size="sm">
            {item.approvedBy
              ? `Approved By: ${item.approvedBy?.name}`
              : "Approved"}
          </Badge>
        );
      }
      if (item.status === TransactionHistoryStatus.Rejected) {
        return (
          <Badge variant="updated" w="80%" size="sm">
            Rejected By: {item.rejectedBy?.name}
          </Badge>
        );
      }
      const tooltipText = getApprovalDisabledTooltipText(item);
      const disabled =
        item.paymentType !== PaymentType.Top ||
        item.orderStatus !== TransactionHistoryOrderStatus.PoSubmission ||
        !!tooltipText;
      return tooltipText ? (
        <Tooltip label={tooltipText} withinPortal multiline>
          <Flex gap={10}>
            <Button
              variant="filledBlue"
              disabled={disabled}
              uppercase
              onClick={() => onApprove(item.id)}
            >
              Approve
            </Button>
            <Button
              variant="outlineBlue"
              disabled={disabled}
              uppercase
              onClick={() => onReject(item.id)}
            >
              Reject
            </Button>
          </Flex>
        </Tooltip>
      ) : (
        <Flex gap={10}>
          <Button
            variant="filledBlue"
            disabled={disabled}
            uppercase
            onClick={() => onApprove(item.id)}
          >
            Approve
          </Button>
          <Button
            variant="outlineBlue"
            disabled={disabled}
            uppercase
            onClick={() => onReject(item.id)}
          >
            Reject
          </Button>
        </Flex>
      );
    },
    [getApprovalDisabledTooltipText, onApprove, onReject]
  );

  const renderTransactionHistoryLimitUsed = useCallback(
    (item: TransactionHistory) => {
      if (!item.limit?.limitName) {
        return null;
      }
      return item.limit.limitName.replace("Top Up", "General");
    },
    []
  );

  const renderPaymentMethod = useCallback(
    (item: TransactionHistory) => {
      if (item.paymentMethod === PaymentMethod.Transfer) {
        return toHumanReadable(item.paymentMethod, true);
      }
      let iconVariant: Variant = "primary";
      const collateral = last(item.collateral);
      if (!collateral) {
        iconVariant = "disabled";
      }
      if (collateral?.status === CollateralStatus.Confirm) {
        iconVariant = "success";
      }
      if (collateral?.status === CollateralStatus.Reject) {
        iconVariant = "error";
      }
      return (
        <Flex align="start" justify="space-between" mih="100%">
          <Text>{toHumanReadable(item.paymentMethod, true)}</Text>
          {canEdit && (
            <ActionIcon
              variant={iconVariant}
              disabled={!item.collateral}
              onClick={() => onChangeCollateral(item)}
            >
              <SvgEdit />
            </ActionIcon>
          )}
        </Flex>
      );
    },
    [canEdit, onChangeCollateral]
  );

  const renderInvoiceNumber = useCallback(
    (item: TransactionHistory) => {
      if (!item.invoice) {
        return null;
      }
      return (
        <Text
          className={classes.link}
          onClick={() =>
            navigate(`?tab=invoicesHistory&invoiceId=${item.invoice?.id}`, {
              replace: true,
            })
          }
        >
          {item.invoice.invoiceNumber}
        </Text>
      );
    },
    [classes.link, navigate]
  );

  const renderPoDataColumn = useCallback((item: TransactionHistory) => {
    return <PODocsModal data={item} />;
  }, []);

  const renderTransactionState = useCallback((item: TransactionHistory) => {
    if (item.orderStatus === TransactionHistoryOrderStatus.Cancel) {
      return (
        <Badge variant="updated" size="sm" radius="xs">
          Cancelled
        </Badge>
      );
    }
    if (item.totalPaid === item.totalOrderAmount) {
      return (
        <Badge color="green" size="sm" radius="xs">
          Complete
        </Badge>
      );
    }
    if (item.totalPaid < item.totalOrderAmount) {
      return (
        <Badge color="yellow" size="sm" radius="xs">
          Ongoing
        </Badge>
      );
    }
  }, []);

  const getRow = useCallback(
    (item: TransactionHistory): TableRow => {
      const conditionPrecedents = item.conditionPrecedents;
      const exceptions = item.exceptions;
      return {
        id: item.id,
        orderDate: toLocalDate(item.orderDate),
        quotationNo: item.quotationNo,
        saleOrderNumber: item.saleOrderNumber,
        invoiceNumber: renderInvoiceNumber(item),
        salesName: item.salesName,
        salesTeam: item.salesTeam,
        limitUsed: renderTransactionHistoryLimitUsed(item),
        paymentType: toHumanReadable(item.paymentType, true),
        poData: renderPoDataColumn(item),
        paymentMethod: renderPaymentMethod(item),
        orderStatus: orderStatusToHumanReadable(item.orderStatus),
        amountTotal: toCurrencyPrice(item.totalOrderAmount),
        paymasters: item.paymasters.length
          ? map(item.paymasters, "name").join(", ")
          : null,
        bastDocuments: item.bastDocuments.length ? (
          <TableFilesDisplay files={map(item.bastDocuments, "file")} />
        ) : null,
        transactionState: renderTransactionState(item),
        hasException: getExceptionColumn(item),
        isRestructured: booleanToString(item?.invoice?.isRestructured),
        conditionPrecedents: (
          <CPExceptionTableCard
            data={conditionPrecedents}
            canEdit={canEdit && !!conditionPrecedents?.length}
            onEdit={() =>
              handleOpenEditModal(ModalType.ConditionPrecedents, item)
            }
          />
        ),
        exceptions: (
          <CPExceptionTableCard
            data={exceptions}
            canEdit={canEdit && !!exceptions?.length}
            onEdit={() => handleOpenEditModal(ModalType.Exceptions, item)}
          />
        ),
        notes: item.notes && <Spoiler text={item.notes} />,
        repayment: renderRepayment(item),
        approval: renderApproval(item),
      };
    },
    [
      renderInvoiceNumber,
      renderTransactionHistoryLimitUsed,
      renderPoDataColumn,
      renderPaymentMethod,
      renderTransactionState,
      getExceptionColumn,
      canEdit,
      renderRepayment,
      renderApproval,
      handleOpenEditModal,
    ]
  );

  const rows = useMemo(() => {
    return data?.map(getRow);
  }, [data, getRow]);

  return [columns, rows];
};
